import React, { Suspense, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Sparkles } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { ASSISTANT_WINDOW_ID } from "./assistantIds";
import { useAssistantLayout, type AssistantLayout } from "./useAssistantLayout";

/**
 * The launcher and the panel's lifetime (ticket 21 §UI; ticket 21.4).
 *
 * The panel is `React.lazy`, so nothing of the assistant loads until somebody
 * opens it — and the whole widget is only mounted where `assistantEnabled` and
 * an entity both hold, which is why it never exists on `/`, on a public page, or
 * on `/admin/dashboard` (no entity ⇒ no principal ⇒ nothing to authorize).
 *
 * Since 21.4 the launcher is a toggle with a fixed name and `aria-expanded`, and
 * the desktop window is rendered right after it, so it follows the launcher in
 * Tab order. If the viewport crosses the window/sheet breakpoint while the
 * assistant is open, it closes: re-rendering it as the full-screen sheet would
 * put a modal on top of whatever page modal happened to be open, and the
 * conversation survives a close anyway.
 */
const AssistantPanel = React.lazy(() =>
  import("./AssistantPanel").then((m) => ({ default: m.AssistantPanel }))
);

export function AssistantWidget() {
  const { t } = useTranslation();
  const { assistantEnabled, entityType, entityId } = useAuth();
  const layout = useAssistantLayout();
  /** The layout the assistant was opened in; `null` while it is closed. */
  const [openedIn, setOpenedIn] = useState<AssistantLayout | null>(null);
  // Once opened, the panel stays mounted so closing keeps the conversation and
  // the draft — "closing is reversible" in the design's state table.
  const [everOpened, setEverOpened] = useState(false);
  const launcherRef = useRef<HTMLButtonElement>(null);

  // Crossing the breakpoint closes the assistant, and it is decided *during
  // render* (code review 21.4, F2). Closing from an effect let the other shell
  // mount open for one commit first: the sheet's exit animation then flashed a
  // full-screen modal and backdrop over whatever page modal was open.
  if (openedIn !== null && openedIn !== layout) {
    setOpenedIn(null);
  }
  const open = openedIn !== null && openedIn === layout;

  if (!assistantEnabled || !entityType || !entityId) return null;

  const handleToggle = () => {
    if (open) {
      setOpenedIn(null);
      return;
    }
    setEverOpened(true);
    setOpenedIn(layout);
  };

  const handleOpenChange = (next: boolean) => setOpenedIn(next ? layout : null);

  return (
    <>
      <button
        ref={launcherRef}
        type="button"
        // One name in both states; the state is `aria-expanded` (21.4, F6).
        aria-label={t("assistant.action.open")}
        aria-expanded={open}
        aria-controls={open && layout === "window" ? ASSISTANT_WINDOW_ID : undefined}
        // Above the mobile navigation and clear of the safe area.
        className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-40 flex min-h-12 min-w-12 items-center justify-center gap-2 rounded-full border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground shadow-lg transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={handleToggle}
      >
        {open ? (
          <ChevronDown className="h-5 w-5" aria-hidden="true" />
        ) : (
          <Sparkles className="h-5 w-5 text-primary" aria-hidden="true" />
        )}
        <span className="hidden sm:inline" aria-hidden="true">
          {t("assistant.action.open")}
        </span>
      </button>

      {everOpened ? (
        <Suspense fallback={null}>
          <AssistantPanel
            open={open}
            onOpenChange={handleOpenChange}
            layout={layout}
            launcherRef={launcherRef}
          />
        </Suspense>
      ) : null}
    </>
  );
}
